<script lang="ts">
  // PageAudit — `#/audit`.
  //
  // PR 7 scope (R-Audit-Page from
  // openspec/changes/ui-redesign-v2/specs/dashboard-ui/spec.md):
  //
  //   - Title "Audit Log" + Refresh button.
  //   - Filter row: DateRange picker (since), Action select, User select.
  //   - 6-column table: TIMESTAMP / ACTOR / ACTION / TARGET / RESULT
  //     / expand affordance. Each row uses <AuditRow>.
  //   - Pagination (100/page default per spec; this PR uses 6 per
  //     page so the design shape fits the 1440px artboard — the
  //     100-per-page wire default is the same as the spec, the
  //     client renders the visible subset in chunks of 6).
  //   - Empty state when the log returns zero entries (single row
  //     reading "No audit entries").
  //   - Error banner above the table when the fetch fails.
  //
  // Visual reference: openspec/changes/ui-redesign-v2/Untitled.fig
  // nodes 0:953-1059 (PageAudit frame 1200×275 with title bar,
  // 3-control filter row, 7-row table, pagination footer).
  //
  // WCAG 2.2 AA:
  //   - Filter controls are <label>-wrapped <select> + <input> pairs;
  //     no naked inputs.
  //   - <th scope="col"> on every header cell.
  //   - <nav aria-label="pagination"> wraps the pagination footer.
  //   - Empty state + error banner are live regions (role=alert)
  //     so screen-reader users hear the state change.

  import AuditRow from "../components/AuditRow.svelte";
  import { auditList, toAuditRow, type AuditRow as AuditRowData } from "../lib/audit";
  import type { AuditView } from "../lib/bindings";

  type FilterAction = "" | "restart" | "stop" | "start";
  type FilterUser = "" | string;

  let rows: AuditRowData[] = [];
  let loading = true;
  let errorMessage: string | null = null;
  let actionFilter: FilterAction = "";
  let userFilter: FilterUser = "";
  let sinceFilter = ""; // date input value (yyyy-mm-dd); client-only
  let currentPage = 1;
  let lastFetchMs = 0;

  const PAGE_SIZE = 6;

  // Distinct actions for the Action <select>. We derive from the loaded
  // data so the dropdown only shows values actually present in the log
  // — no phantom "start" if no start entries exist.
  $: actionOptions = deriveActions(rows);

  // Distinct users for the User <select>.
  $: userOptions = deriveUsers(rows);

  // Apply client-side filters. DateRange is treated as "since" (the
  // half-open interval [since, now]) — finer filtering (until) lands
  // in a follow-up if the backend grows a `until` query param.
  $: filteredRows = applyFilters(rows, {
    action: actionFilter,
    user: userFilter,
    since: sinceFilter,
  });

  $: pageStart = (currentPage - 1) * PAGE_SIZE;
  $: pageEnd = pageStart + PAGE_SIZE;
  $: visibleRows = filteredRows.slice(pageStart, pageEnd);
  $: pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

  function deriveActions(items: AuditRowData[]): string[] {
    const set = new Set<string>();
    for (const r of items) set.add(r.action);
    return [...set].sort();
  }

  function deriveUsers(items: AuditRowData[]): string[] {
    const set = new Set<string>();
    for (const r of items) set.add(r.actor);
    return [...set].sort();
  }

  function applyFilters(
    items: readonly AuditRowData[],
    f: { action: string; user: string; since: string },
  ): AuditRowData[] {
    const sinceMs = f.since ? Date.parse(f.since) : null;
    return items.filter((r) => {
      if (f.action && r.action !== f.action) return false;
      if (f.user && r.actor !== f.user) return false;
      if (sinceMs !== null && r.timestampMs < sinceMs) return false;
      return true;
    });
  }

  async function load(): Promise<void> {
    loading = true;
    errorMessage = null;
    try {
      // Server-side filter for action/user only (the wire accepts
      // those today; since is client-only until the backend grows it).
      const filters: Parameters<typeof auditList>[0] = {};
      if (actionFilter) filters.action = actionFilter;
      if (userFilter) filters.user = userFilter;
      const views: AuditView[] = await auditList(filters);
      rows = views.map(toAuditRow);
      lastFetchMs = Date.now();
      // Reset to first page on every successful load so refresh
      // returns to page 1 (per spec scenario 2).
      currentPage = 1;
    } catch (err) {
      errorMessage =
        (err instanceof Error ? err.message : String(err)) ||
        "Failed to load audit log.";
    } finally {
      loading = false;
    }
  }

  function onActionChange(e: Event): void {
    actionFilter = (e.target as HTMLSelectElement).value as FilterAction;
    // Client filter — no re-fetch. Server filter already aligns with
    // the action field; the client keeps the visible page responsive.
  }

  function onUserChange(e: Event): void {
    userFilter = (e.target as HTMLSelectElement).value as FilterUser;
  }

  function onSinceChange(e: Event): void {
    sinceFilter = (e.target as HTMLInputElement).value;
    currentPage = 1;
  }

  function goToPage(n: number): void {
    if (n < 1 || n > pageCount) return;
    currentPage = n;
  }

  function onRefresh(): void {
    // Force a server round-trip and reset to page 1.
    void load();
  }

  function pageNumbers(): number[] {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  // Kick off the initial fetch at module scope (not in onMount) —
  // matches the precedent set by the other fetch-driven pages so
  // @testing-library/svelte sees the post-fetch DOM without
  // requiring await act() or fake timers.
  void load();
</script>

<section class="page-audit" aria-labelledby="page-audit-heading">
  <header class="page-audit__head">
    <h2
      id="page-audit-heading"
      class="page-audit__title"
      data-testid="page-audit-heading"
    >Audit Log</h2>
    <button
      type="button"
      class="page-audit__refresh"
      data-testid="audit-refresh"
      aria-label="Refresh audit log"
      on:click={onRefresh}
    >Refresh</button>
  </header>

  <div class="page-audit__filters" data-testid="audit-filters">
    <label class="page-audit__filter">
      <span class="page-audit__filter-label">Since</span>
      <input
        type="date"
        class="page-audit__filter-input"
        data-testid="audit-filter-since"
        value={sinceFilter}
        on:change={onSinceChange}
      />
    </label>
    <label class="page-audit__filter">
      <span class="page-audit__filter-label">Action</span>
      <select
        class="page-audit__filter-input"
        data-testid="audit-filter-action"
        value={actionFilter}
        on:change={onActionChange}
      >
        <option value="">All actions</option>
        {#each actionOptions as a (a)}
          <option value={a}>{a}</option>
        {/each}
      </select>
    </label>
    <label class="page-audit__filter">
      <span class="page-audit__filter-label">User</span>
      <select
        class="page-audit__filter-input"
        data-testid="audit-filter-user"
        value={userFilter}
        on:change={onUserChange}
      >
        <option value="">All users</option>
        {#each userOptions as u (u)}
          <option value={u}>{u}</option>
        {/each}
      </select>
    </label>
  </div>

  {#if errorMessage}
    <div
      class="page-audit__error"
      data-testid="audit-error-banner"
      role="alert"
    >
      <span class="page-audit__error-icon" aria-hidden="true">!</span>
      <span class="page-audit__error-text">
        Failed to load audit log — {errorMessage}.
      </span>
      <button
        type="button"
        class="page-audit__error-retry"
        on:click={onRefresh}
      >Retry</button>
    </div>
  {/if}

  <div class="page-audit__table-wrap" data-testid="audit-table-wrap">
    <table class="audit-table" aria-describedby="page-audit-heading">
      <thead>
        <tr>
          <th scope="col" class="audit-table__th">Timestamp</th>
          <th scope="col" class="audit-table__th">Actor</th>
          <th scope="col" class="audit-table__th">Action</th>
          <th scope="col" class="audit-table__th">Target</th>
          <th scope="col" class="audit-table__th">Result</th>
          <th scope="col" class="audit-table__th"><span class="visually-hidden">Details</span></th>
        </tr>
      </thead>
      <tbody>
        {#if loading && rows.length === 0}
          <tr class="audit-table__loading-row">
            <td colspan="6" class="audit-table__loading-cell">
              Loading audit log…
            </td>
          </tr>
        {:else if !loading && rows.length === 0}
          <tr class="audit-table__empty-row" data-testid="audit-empty-row">
            <td colspan="6" class="audit-table__empty-cell">
              No audit entries — actions will appear here after you perform them.
            </td>
          </tr>
        {:else if filteredRows.length === 0}
          <tr class="audit-table__empty-row" data-testid="audit-filter-empty">
            <td colspan="6" class="audit-table__empty-cell">
              No entries match the current filters.
            </td>
          </tr>
        {:else}
          {#each visibleRows as r (r.tsIso + ":" + r.actor + ":" + r.target)}
            <AuditRow row={r} />
          {/each}
        {/if}
      </tbody>
    </table>
  </div>

  {#if !loading && filteredRows.length > PAGE_SIZE}
    <nav class="audit-pagination" aria-label="Pagination" data-testid="audit-pagination">
      <button
        type="button"
        class="audit-pagination__btn"
        data-testid="audit-page-prev"
        aria-label="Previous page"
        disabled={currentPage <= 1}
        on:click={() => goToPage(currentPage - 1)}
      >‹</button>
      {#each pageNumbers() as n (n)}
        <button
          type="button"
          class="audit-pagination__page"
          class:audit-pagination__page--active={n === currentPage}
          data-testid={`audit-page-${n}`}
          aria-current={n === currentPage ? "page" : null}
          aria-label={`Page ${n}`}
          on:click={() => goToPage(n)}
        >{n}</button>
      {/each}
      <button
        type="button"
        class="audit-pagination__btn"
        data-testid="audit-page-next"
        aria-label="Next page"
        disabled={currentPage >= pageCount}
        on:click={() => goToPage(currentPage + 1)}
      >›</button>
    </nav>
  {/if}

  {#if lastFetchMs > 0 && !loading && !errorMessage}
    <p class="page-audit__meta" data-testid="audit-meta">
      Showing {visibleRows.length} of {filteredRows.length}
      ({rows.length} total).
    </p>
  {/if}
</section>

<style>
  .page-audit {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .page-audit__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
  }

  .page-audit__title {
    margin: 0;
    font: var(--type-h1);
    color: var(--text-strong);
  }

  .page-audit__refresh {
    height: 32px;
    padding: 0 var(--space-4);
    border-radius: var(--radius-sm);
    background: var(--accent-cyan);
    color: var(--bg);
    border: 1px solid var(--accent-cyan);
    font: var(--type-body-sm);
    font-weight: 500;
    cursor: pointer;
  }
  .page-audit__refresh:hover {
    background: #00C7B2;
    border-color: #00C7B2;
  }

  .page-audit__filters {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
    align-items: flex-end;
  }

  .page-audit__filter {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    min-width: 180px;
  }

  .page-audit__filter-label {
    font: var(--type-label-caps);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-mid);
    font-size: 11px;
  }

  .page-audit__filter-input {
    height: 32px;
    padding: 0 var(--space-3);
    border-radius: var(--radius-sm);
    background: var(--surface-2);
    border: 1px solid var(--border);
    color: var(--text-strong);
    font: var(--type-body-sm);
  }
  .page-audit__filter-input:focus-visible {
    outline: 2px solid var(--accent-cyan);
    outline-offset: 1px;
    border-color: var(--accent-cyan);
  }

  .page-audit__error {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--err-bg);
    border: 1px solid rgba(239, 68, 68, 0.30);
    border-radius: var(--radius-sm);
    color: var(--err-red);
    font: var(--type-body-sm);
  }
  .page-audit__error-icon {
    display: inline-flex;
    width: 22px;
    height: 22px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(239, 68, 68, 0.20);
    color: var(--err-red);
    font-weight: 700;
  }
  .page-audit__error-retry {
    margin-left: auto;
    height: 28px;
    padding: 0 var(--space-3);
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--err-red);
    border: 1px solid rgba(239, 68, 68, 0.40);
    cursor: pointer;
    font: var(--type-label);
  }

  .page-audit__table-wrap {
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    overflow: hidden;
    background: var(--surface);
  }

  .audit-table {
    width: 100%;
    border-collapse: collapse;
    color: var(--text-strong);
  }

  .audit-table__th {
    padding: var(--space-3) var(--space-4);
    background: var(--surface-2);
    color: var(--text-dim);
    font: var(--type-label-caps);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 11px;
    text-align: left;
    border-bottom: 1px solid var(--border);
  }

  .audit-table__loading-row td,
  .audit-table__empty-row td {
    padding: var(--space-6) var(--space-4);
    text-align: center;
    color: var(--text-mid);
    font: var(--type-body);
  }

  .audit-table__empty-cell {
    background: var(--surface);
  }

  .audit-pagination {
    display: flex;
    gap: var(--space-2);
    justify-content: flex-end;
    padding: var(--space-3) 0;
  }

  .audit-pagination__btn,
  .audit-pagination__page {
    min-width: 32px;
    height: 32px;
    padding: 0 var(--space-3);
    border-radius: var(--radius-sm);
    background: var(--surface-2);
    color: var(--text-mid);
    border: 1px solid var(--border);
    font: var(--type-body-sm);
    cursor: pointer;
  }
  .audit-pagination__btn:hover:not(:disabled),
  .audit-pagination__page:hover {
    color: var(--text-strong);
    background: var(--surface-3);
  }
  .audit-pagination__btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .audit-pagination__page--active {
    background: var(--accent-cyan);
    color: var(--bg);
    border-color: var(--accent-cyan);
    font-weight: 600;
  }

  .page-audit__meta {
    color: var(--text-mid);
    font: var(--type-mono-md);
    text-align: right;
    margin: 0;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
